import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_BASE_URL, { fetchApi } from '../services/api';
import { CalendarPlus, ExternalLink, Download, Clock, Mail, Compass, ChevronDown, ChevronUp } from 'lucide-react';
import useDarkMode from '../hooks/useDarkMode';
import ThemeToggle from '../components/ThemeToggle';
import AdBanner from '../components/AdBanner';

const parseLocalDate = (str) => new Date(String(str).replace(' ', 'T').replace(/\.000Z$|Z$/, ''));

function DiscoverSection({ currentChannelId, currentGroupId, onGroupClick }) {
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    fetchApi('/public_full/discover')
      .then(data => {
        setChannels(data);
        const init = {};
        data.forEach(ch => { init[ch.id] = true; });
        setExpanded(init);
      })
      .catch(() => {});
  }, []);

  if (channels.length === 0) return null;

  const handleGroupClick = (channelId, groupId) => {
    onGroupClick?.();
    navigate(`/public/${channelId}/${groupId}`);
  };

  return (
    <div style={{
      padding: '1rem',
      background: 'var(--bg-elevated)',
      border: '1.5px solid var(--border-color)',
      borderTop: '3px solid var(--primary-500)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow-sm)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '0.85rem' }}>
        <Compass size={15} style={{ color: 'var(--primary-500)', flexShrink: 0 }} />
        <span style={{ fontSize: '0.875rem', fontWeight: '700', color: 'var(--text-primary)' }}>
          Keşfet
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        {channels.filter(channel => String(channel.id) !== String(currentChannelId)).map(channel => {
          const isOpen = expanded[channel.id];
          return (
            <div key={channel.id} style={{
              border: '1.5px solid var(--border-color)',
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
            }}>
              <button
                onClick={() => setExpanded(prev => ({ ...prev, [channel.id]: !prev[channel.id] }))}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.55rem 0.75rem',
                  background: 'var(--bg-secondary)',
                  border: 'none',
                  cursor: 'pointer',
                  gap: '6px',
                }}
              >
                <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-primary)', textAlign: 'left' }}>
                  {channel.name}
                </span>
                {isOpen
                  ? <ChevronUp size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  : <ChevronDown size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                }
              </button>

              {isOpen && channel.groups.length > 0 && (
                <div style={{ padding: '0.4rem 0.5rem', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {channel.groups
                    .map(group => (
                      <button
                        key={group.id}
                        onClick={() => handleGroupClick(channel.id, group.id)}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          padding: '0.38rem 0.6rem',
                          borderRadius: 'var(--radius-sm)',
                          border: 'none',
                          background: 'transparent',
                          color: 'var(--text-secondary)',
                          fontSize: '0.75rem',
                          fontWeight: '400',
                          cursor: 'pointer',
                          transition: 'background var(--transition-fast)',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-secondary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        {group.name}
                      </button>
                    ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function PublicEventView() {
  const { channelId, groupId } = useParams();
  const navigate = useNavigate();

  const [channelName, setChannelName] = useState('');
  const [channelUrl, setChannelUrl] = useState(null);
  const [groups, setGroups] = useState([]);
  const [events, setEvents] = useState([]);
  const [currentGroupId, setCurrentGroupId] = useState(groupId || null);
  const [loading, setLoading] = useState(true);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const { darkMode, toggle: toggleDark } = useDarkMode();

  useEffect(() => {
    return () => {
      document.title = 'Pupl';
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', 'Pupl - Etkinliklerini kolayca planla ve paylaş.');
      const existing = document.getElementById('pupl-jsonld');
      if (existing) existing.remove();
      ['og:title', 'og:description', 'og:url', 'og:type', 'og:site_name',
       'twitter:card', 'twitter:title', 'twitter:description'].forEach(key => {
        const el = document.querySelector(`meta[property="${key}"], meta[name="${key}"]`);
        if (el) el.remove();
      });
      const canonical = document.getElementById('pupl-canonical');
      if (canonical) canonical.remove();
    };
  }, []);

  useEffect(() => {
    if (!events.length || !channelName) return;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: channelName,
      itemListElement: events.map((event, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Event',
          name: event.title,
          startDate: new Date(String(event.start_time).replace(' ', 'T').replace(/\.000Z$|Z$/, '')).toISOString(),
          ...(event.description && { description: event.description }),
          ...(event.url && { url: event.url }),
          organizer: { '@type': 'Organization', name: channelName, url: 'https://pupl.net' },
          eventStatus: 'https://schema.org/EventScheduled',
          eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
        },
      })),
    };

    let tag = document.getElementById('pupl-jsonld');
    if (!tag) {
      tag = document.createElement('script');
      tag.id = 'pupl-jsonld';
      tag.type = 'application/ld+json';
      document.head.appendChild(tag);
    }
    tag.textContent = JSON.stringify(schema);
  }, [events, channelName]);

  useEffect(() => {
    if (!channelId) return;

    const loadData = async () => {
      try {
        setLoading(true);
        const endpoint = groupId
          ? `/public_full/${channelId}/${groupId}`
          : `/public_full/${channelId}`;
        const data = await fetchApi(endpoint);
        setChannelName(data.channelName);
        setChannelUrl(data.channelUrl || null);
        setGroups(data.groups);
        setEvents(Array.isArray(data.events) ? data.events : []);
        if (!groupId && data.selectedGroupId) {
          setCurrentGroupId(String(data.selectedGroupId));
        } else {
          setCurrentGroupId(groupId || null);
        }

        const activeGroupId = groupId || data.selectedGroupId;
        const activeGroup = data.groups?.find(g => String(g.id) === String(activeGroupId));
        const pageTitle = activeGroup
          ? `${activeGroup.name} | ${data.channelName} - Pupl`
          : `${data.channelName} - Pupl`;
        const pageDesc = activeGroup
          ? `${data.channelName} - ${activeGroup.name} etkinlik takvimi. Etkinlikleri takviminize ekleyin.`
          : `${data.channelName} etkinlik takvimi. Etkinlikleri takviminize ekleyin.`;
        document.title = pageTitle;
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) metaDesc.setAttribute('content', pageDesc);

        const canonicalUrl = groupId
          ? `https://pupl.net/public/${channelId}/${groupId}`
          : `https://pupl.net/public/${channelId}`;

        const setMeta = (attrs) => {
          const selector = attrs.property
            ? `meta[property="${attrs.property}"]`
            : `meta[name="${attrs.name}"]`;
          let el = document.querySelector(selector);
          if (!el) {
            el = document.createElement('meta');
            document.head.appendChild(el);
          }
          Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        };

        setMeta({ property: 'og:type', content: 'website' });
        setMeta({ property: 'og:site_name', content: 'Pupl' });
        setMeta({ property: 'og:title', content: pageTitle });
        setMeta({ property: 'og:description', content: pageDesc });
        setMeta({ property: 'og:url', content: canonicalUrl });
        setMeta({ name: 'twitter:card', content: 'summary' });
        setMeta({ name: 'twitter:title', content: pageTitle });
        setMeta({ name: 'twitter:description', content: pageDesc });

        let canonical = document.getElementById('pupl-canonical');
        if (!canonical) {
          canonical = document.createElement('link');
          canonical.id = 'pupl-canonical';
          canonical.rel = 'canonical';
          document.head.appendChild(canonical);
        }
        canonical.href = canonicalUrl;
      } catch (err) {
        console.error("Veri alınamadı:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [channelId, groupId]);

  const handleGroupSelect = (newGroupId) => {
    navigate(`/public/${channelId}/${newGroupId}`);
  };

  // Twitter/X in-app browser (WKWebView) webcal:// şemasını desteklemiyor.
  const isTwitterBrowser = /Twitter/i.test(navigator.userAgent);

  /**
   * iOS'ta webcal:// şeması kullanarak doğrudan Calendar uygulamasını açar.
   * webcal:// OS seviyesinde intercept edilir, tüm iOS in-app browser'larda çalışır.
   * Android'de https:// ile download tetiklenir.
   */
  const openICSUrl = (httpsUrl) => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const url = isIOS ? httpsUrl.replace(/^https?:\/\//, 'webcal://') : httpsUrl;
    window.location.href = url;
  };

  const withUtm = (url) => {
    if (!url) return url;
    try {
      const u = new URL(url);
      u.searchParams.set('utm_source', 'pupl');
      u.searchParams.set('utm_medium', 'referral');
      return u.toString();
    } catch {
      return url;
    }
  };

  const icsUrl = currentGroupId
    ? `${API_BASE_URL}/public_full/ics/${channelId}/${currentGroupId}`
    : `${API_BASE_URL}/public_full/ics/${channelId}`;

  return (
    <div style={{
      fontFamily: "'Inter', sans-serif",
      minHeight: '100vh',
      background: 'var(--bg-primary)',
      color: 'var(--text-primary)',
      padding: '1rem',
      paddingBottom: '100px',
      transition: 'background var(--transition-normal), color var(--transition-normal)',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '1.5rem',
        maxWidth: '1200px',
        margin: '1.5rem auto',
        alignItems: 'flex-start',
      }}>

        {/* Left Sidebar Ad */}
        <div className="ad-sidebar" style={{
          width: '160px',
          flexShrink: 0,
          position: 'sticky',
          top: '1.5rem',
        }}>
          <AdBanner
            adSlot="8592470415"
            adFormat="vertical"
            style={{ width: '160px', height: '600px' }}
          />
        </div>

        {/* Main Content */}
        <div style={{
          width: '100%',
          maxWidth: '700px',
          flex: '1 1 auto',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}>

          {/* Main Card */}
          <div style={{
            padding: '1.25rem',
            background: 'var(--bg-elevated)',
            border: '1.5px solid var(--border-color)',
            borderTop: '3px solid var(--accent-500)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-xl)',
            animation: 'fadeIn 0.4s ease-out',
          }}>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.65rem' }}>
              <div
                onClick={() => navigate('/')}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
              >
                <img src="/favicon.svg" alt="Pupl Logo" style={{ width: '28px', height: '28px', borderRadius: 'var(--radius-sm)' }} />
                <span style={{
                  fontSize: '1rem',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, var(--accent-500), var(--accent-400))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em',
                }}>
                  Pupl
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {!loading && events.length > 0 && !isTwitterBrowser && (
                  <button
                    onClick={() => openICSUrl(icsUrl)}
                    style={{
                      background: 'var(--accent-500)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 'var(--radius-md)',
                      padding: '0.38rem 0.7rem',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      transition: 'background var(--transition-fast)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--accent-600)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--accent-500)'; }}
                  >
                    <Download size={13} />
                    Takvime Aktar
                  </button>
                )}
                <ThemeToggle darkMode={darkMode} onToggle={toggleDark} />
              </div>
            </div>

            {/* Channel Name */}
            <h2 style={{
              textAlign: 'center',
              margin: '0 0 0.3rem 0',
              fontSize: '1.375rem',
              fontWeight: '700',
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em',
            }}>
              {channelName}
            </h2>

            {/* Channel Link */}
            {channelUrl && (
              <div style={{ textAlign: 'center', marginBottom: '0.4rem' }}>
                <a
                  href={withUtm(channelUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '0.75rem',
                    color: 'var(--primary-500)',
                    textDecoration: 'none',
                    fontWeight: '500',
                  }}
                >
                  <ExternalLink size={12} />
                  {channelUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                </a>
              </div>
            )}

            {!loading && events.length > 0 && isTwitterBrowser && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'var(--bg-secondary)',
                border: '1.5px solid var(--border-color)',
                borderRadius: 'var(--radius-md)',
                padding: '0.45rem 0.75rem',
                marginBottom: '0.5rem',
                fontSize: '0.75rem',
                color: 'var(--text-secondary)',
              }}>
                <CalendarPlus size={13} style={{ flexShrink: 0, color: 'var(--accent-500)' }} />
                Takvime eklemek için sayfayı tarayıcınızda açın.
              </div>
            )}

            {/* Group Tabs */}
            <div style={{
              overflowX: 'auto',
              whiteSpace: 'nowrap',
              marginBottom: '0.75rem',
              padding: '2px 0',
              display: 'flex',
              gap: '0.4rem',
            }}>
              {groups.map(group => {
                const isActive = group.id === parseInt(currentGroupId);
                return (
                  <button
                    key={group.id}
                    onClick={() => handleGroupSelect(group.id)}
                    style={{
                      display: 'inline-block',
                      padding: '0.4rem 0.85rem',
                      borderRadius: 'var(--radius-full)',
                      border: `1.5px solid ${isActive ? 'transparent' : 'var(--border-color)'}`,
                      background: isActive
                        ? 'linear-gradient(135deg, var(--accent-500), var(--accent-600))'
                        : 'var(--bg-secondary)',
                      color: isActive ? '#fff' : 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.75rem',
                      transition: 'all var(--transition-fast)',
                      boxShadow: isActive ? '0 3px 10px rgba(230, 126, 34, 0.25)' : 'none',
                      flexShrink: 0,
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) e.currentTarget.style.borderColor = 'var(--primary-300)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) e.currentTarget.style.borderColor = 'var(--border-color)';
                    }}
                  >
                    {group.name}
                  </button>
                );
              })}
            </div>

            {/* Events */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  border: '3px solid var(--border-color)',
                  borderTopColor: 'var(--primary-500)',
                  borderRadius: '50%',
                  animation: 'spin 0.6s linear infinite',
                  margin: '0 auto 0.75rem',
                }} />
                Yükleniyor...
              </div>
            ) : events.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-muted)' }}>
                <CalendarPlus size={32} style={{ marginBottom: '0.75rem', opacity: 0.5 }} />
                <p>Bu grupta etkinlik yok.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {(() => {
                  const grouped = {};
                  events.forEach(event => {
                    const d = parseLocalDate(event.start_time);
                    const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                    if (!grouped[dateKey]) grouped[dateKey] = [];
                    grouped[dateKey].push(event);
                  });
                  const sortedDates = Object.keys(grouped).sort();
                  let globalIndex = 0;

                  return sortedDates.map(dateKey => {
                    const [y, m, d] = dateKey.split('-');
                    const dateLabel = `${d}.${m}.${y}`;
                    const sortedEvents = grouped[dateKey].sort((a, b) => parseLocalDate(a.start_time) - parseLocalDate(b.start_time));

                    return (
                      <div key={dateKey}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.6rem',
                          marginBottom: '0.5rem',
                        }}>
                          <div style={{
                            background: 'var(--accent-500)',
                            color: '#fff',
                            padding: '0.3rem 0.8rem',
                            borderRadius: 'var(--radius-full)',
                            fontWeight: '700',
                            fontSize: '0.75rem',
                            letterSpacing: '0.02em',
                          }}>
                            {dateLabel}
                          </div>
                          <div style={{
                            flex: 1,
                            height: '1.5px',
                            background: 'var(--border-color)',
                            borderRadius: 'var(--radius-full)',
                          }} />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                          {sortedEvents.map((event) => {
                            const idx = globalIndex++;
                            return (
                              <div
                                key={event.id}
                                style={{
                                  background: 'var(--bg-secondary)',
                                  borderRadius: 'var(--radius-md)',
                                  border: '1.5px solid var(--border-color)',
                                  padding: '0.9rem 1rem',
                                  boxShadow: 'var(--shadow-sm)',
                                  transition: 'all var(--transition-fast)',
                                  animation: `fadeIn 0.3s ease-out ${idx * 0.05}s both`,
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                                  e.currentTarget.style.borderColor = 'var(--primary-200)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                                  e.currentTarget.style.borderColor = 'var(--border-color)';
                                }}
                              >
                                <h3 style={{
                                  fontSize: '1rem',
                                  margin: '0 0 0.3rem 0',
                                  color: 'var(--text-primary)',
                                  fontWeight: '600',
                                }}>
                                  {event.title}
                                </h3>

                                {event.description && (
                                  <p style={{
                                    margin: '0 0 0.5rem 0',
                                    fontSize: '0.875rem',
                                    color: 'var(--text-secondary)',
                                    lineHeight: '1.5',
                                  }}>
                                    {event.description}
                                  </p>
                                )}

                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginTop: '0.4rem',
                                }}>
                                  <div style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                    background: 'var(--primary-50)',
                                    color: 'var(--primary-400)',
                                    padding: '0.28rem 0.65rem',
                                    borderRadius: 'var(--radius-full)',
                                    fontWeight: '600',
                                    fontSize: '0.75rem',
                                  }}>
                                    <Clock size={13} />
                                    {parseLocalDate(event.start_time).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                  </div>

                                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                                    {event.url && (
                                      <a
                                        href={withUtm(event.url)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          background: 'var(--primary-50)',
                                          borderRadius: 'var(--radius-sm)',
                                          width: '34px',
                                          height: '34px',
                                          display: 'flex',
                                          justifyContent: 'center',
                                          alignItems: 'center',
                                          color: 'var(--primary-500)',
                                          textDecoration: 'none',
                                          border: '1.5px solid var(--primary-200)',
                                          transition: 'all var(--transition-fast)',
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--primary-100)'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--primary-50)'; }}
                                      >
                                        <ExternalLink size={15} />
                                      </a>
                                    )}
                                    <button
                                      onClick={() => openICSUrl(`${API_BASE_URL}/public_full/event-ics/${event.id}`)}
                                      style={{
                                        background: 'var(--accent-50)',
                                        border: '1.5px solid var(--accent-100)',
                                        borderRadius: 'var(--radius-sm)',
                                        width: '34px',
                                        height: '34px',
                                        cursor: 'pointer',
                                        color: 'var(--accent-500)',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        transition: 'all var(--transition-fast)',
                                      }}
                                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--accent-100)'; }}
                                      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--accent-50)'; }}
                                    >
                                      <CalendarPlus size={15} />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>

          {/* ICS Info Section */}
          {!loading && events.length > 0 && (
            <div style={{
              padding: '1.25rem',
              background: 'var(--bg-elevated)',
              border: '1.5px solid var(--border-color)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-sm)',
            }}>
              <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', fontWeight: '700', color: 'var(--text-primary)' }}>
                Takvime nasıl eklenir?
              </h4>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div>
                  <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.75rem', fontWeight: '600', color: 'var(--accent-500)' }}>iPhone / iPad (Apple Takvim)</p>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                    "Takvime Aktar" butonuna dokunun → Takvim uygulaması otomatik açılır → "Abone Ol" seçeneğine dokunun.
                  </p>
                </div>

                <div>
                  <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.75rem', fontWeight: '600', color: 'var(--accent-500)' }}>Android</p>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                    Dosyayı indirin → Takvim uygulamasıyla açın → "Kaydet" ile ekleyin.
                  </p>
                </div>

                <div>
                  <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.75rem', fontWeight: '600', color: 'var(--accent-500)' }}>Google Takvim</p>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                    calendar.google.com → Ayarlar → İçe ve Dışa Aktar → İçe Aktar → .ics dosyasını seçin.
                  </p>
                </div>

                <div style={{
                  background: 'var(--primary-50)',
                  border: '1px solid var(--primary-200)',
                  borderRadius: 'var(--radius-md)',
                  padding: '0.6rem 0.75rem',
                  fontSize: '0.75rem',
                  color: 'var(--primary-500)',
                  lineHeight: '1.5',
                }}>
                  Tek etkinlik eklemek için etkinlik kartındaki <CalendarPlus size={12} style={{ verticalAlign: 'middle' }} /> butonunu kullanın.
                </div>
              </div>
            </div>
          )}

          {/* Feedback Section */}
          <div style={{
            padding: '1rem 1.25rem',
            background: 'var(--bg-elevated)',
            border: '1.5px solid var(--border-color)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-sm)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: 'var(--radius-full)',
              background: 'var(--primary-50)',
              border: '1.5px solid var(--primary-200)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexShrink: 0,
            }}>
              <Mail size={16} style={{ color: 'var(--primary-500)' }} />
            </div>
            <div>
              <p style={{ margin: '0 0 0.15rem 0', fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                Hata mı buldunuz?
              </p>
              <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                Geri bildirim ve hata raporları için:{' '}
                <a
                  href="mailto:pupleasy@gmail.com"
                  style={{ color: 'var(--primary-500)', fontWeight: '600', textDecoration: 'none' }}
                >
                  pupleasy@gmail.com
                </a>
              </p>
            </div>
          </div>

        </div>

        {/* Right Sidebar - Keşfet (Web only) */}
        <div className="discover-sidebar" style={{
          width: '260px',
          flexShrink: 0,
          position: 'sticky',
          top: '1.5rem',
        }}>
          <DiscoverSection currentChannelId={channelId} currentGroupId={currentGroupId} />
        </div>

      </div>

      {/* Keşfet FAB - Mobile Only */}
      <button
        className="discover-fab"
        onClick={() => setDiscoverOpen(true)}
      >
        <Compass size={18} />
        Keşfet
      </button>

      {/* Overlay */}
      {discoverOpen && (
        <div
          className="discover-overlay"
          onClick={() => setDiscoverOpen(false)}
        />
      )}

      {/* Drawer */}
      <div className={`discover-drawer${discoverOpen ? ' open' : ''}`}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
          <button
            onClick={() => setDiscoverOpen(false)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1.1rem', lineHeight: 1, padding: '4px' }}
          >
            ✕
          </button>
        </div>
        <DiscoverSection currentChannelId={channelId} currentGroupId={currentGroupId} onGroupClick={() => setDiscoverOpen(false)} />
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 960px) {
          .ad-sidebar { display: none !important; }
          .discover-sidebar { display: none !important; }
          .discover-fab { display: flex !important; }
          .discover-drawer { display: block !important; }
        }
        @media (min-width: 961px) {
          .discover-sidebar { display: block !important; }
          .discover-fab { display: none !important; }
          .discover-drawer { display: none !important; }
          .discover-overlay { display: none !important; }
        }
        .discover-fab {
          display: none;
          position: fixed;
          bottom: 1.5rem;
          right: 1rem;
          background: var(--primary-500);
          color: #fff;
          border: none;
          border-radius: var(--radius-full);
          padding: 0.6rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          align-items: center;
          gap: 6px;
          box-shadow: var(--shadow-md);
          z-index: 150;
        }
        .discover-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.4);
          z-index: 200;
        }
        .discover-drawer {
          display: none;
          position: fixed;
          top: 0;
          right: 0;
          height: 100%;
          width: 300px;
          max-width: 85vw;
          background: var(--bg-elevated);
          box-shadow: -4px 0 24px rgba(0,0,0,0.15);
          z-index: 201;
          transform: translateX(100%);
          transition: transform 0.28s ease;
          overflow-y: auto;
          padding: 1rem;
        }
        .discover-drawer.open { transform: translateX(0); }
      `}</style>
    </div>
  );
}
